import React, { useState, useEffect } from "react";
import { FaCopy, FaCheck, FaThumbsDown } from "react-icons/fa";
import "../styles/Message.css";

const Message = ({ message, formatMessageContent, onReportResponse, messages, selectedProfileUuid, userInfo }) => {
  const [copied, setCopied] = useState(false);
  const [reported, setReported] = useState(false);
  const [showFinalMessage, setShowFinalMessage] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = message.content;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReport = async () => {
    if (!onReportResponse || !selectedProfileUuid) return;

    try {
      // Find the previous user message (query) that led to this response
      const messageIndex = messages.findIndex(msg => msg.id === message.id);
      let previousUserMessage = null;
      
      // Look backward from current message to find the previous user message
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          previousUserMessage = messages[i];
          break;
        }
      }

      if (!previousUserMessage) {
        console.error('Could not find previous user message');
        return;
      }

      // Call the report function with the necessary data
      await onReportResponse({
        response_content: message.content,
        query_content: previousUserMessage.content,
        profile_uuid: selectedProfileUuid,
        username: userInfo?.username || 'Unknown'
      });

      setReported(true);
      setTimeout(() => setReported(false), 3000); // Reset after 3 seconds
    } catch (error) {
      console.error('Error reporting response:', error);
    }
  };

  const isPlaceholder = message.isPlaceholder || message.content === "🔍 Analysing...";

  // Smooth progress animation and final message timing
  useEffect(() => {
    if (!isPlaceholder) {
      setShowFinalMessage(false);
      setProgress(0);
      return;
    }

    // Reset states
    setShowFinalMessage(false);
    setProgress(0);

    // Smooth progress animation - 60 seconds total with easing
    const startTime = Date.now();
    const duration = 60000; // 60 seconds
    
    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      
      // Ease-out-cubic for smooth, non-linear progress
      const easedProgress = 1 - Math.pow(1 - rawProgress, 3);
      setProgress(easedProgress * 100);
      
      if (rawProgress < 1) {
        requestAnimationFrame(animateProgress);
      }
    };
    
    const animationFrame = requestAnimationFrame(animateProgress);

    // Show final message after 50 seconds
    const finalMessageTimer = setTimeout(() => {
      setShowFinalMessage(true);
    }, 50000);

    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrame);
      clearTimeout(finalMessageTimer);
    };
  }, [isPlaceholder]);

  return (
    <div className={`message ${message.role === "user" ? "user" : "assistant"} ${message.isError ? "error" : ""} ${isPlaceholder ? "placeholder" : ""}`}>
      <div className="message-content">
        <div className="message-wrapper">
          {isPlaceholder ? (
            <div className="message-text loading-container">
              <div className="loading-content">
                <div className="loading-row">
                  {/* Lightning bolt icon */}
                  <div className="bolt-wrapper">
                    <svg className="bolt-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="progress-bar-container">
                    <div className="progress-bar-track">
                      <div 
                        className={`progress-bar-fill ${progress >= 99 ? 'complete' : ''}`}
                        style={{ width: `${Math.max(progress, 3)}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Final message after progress completes */}
                {showFinalMessage && (
                  <div className="final-message">
                    <span className="final-message-icon">✨</span>
                    Response will appear here soon
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="message-text" dangerouslySetInnerHTML={{
              __html: formatMessageContent(message.content),
            }} />
          )}
          {message.role === "user" && !isPlaceholder && (
            <div className="message-actions">
              <button 
                className="copy-button user-copy-button" 
                onClick={handleCopy}
                title={copied ? "Copied!" : "Copy query"}
              >
                {copied ? <FaCheck /> : <FaCopy />}
              </button>
            </div>
          )}
          {message.role === "assistant" && !isPlaceholder && (
            <div className="message-actions">
              <button 
                className="copy-button" 
                onClick={handleCopy}
                title={copied ? "Copied!" : "Copy message"}
              >
                {copied ? <FaCheck /> : <FaCopy />}
              </button>
              <button 
                className="report-button" 
                onClick={handleReport}
                title="Report this answer"
                disabled={reported}
              >
                {reported ? <FaCheck /> : <FaThumbsDown />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;